import { Close as CloseIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useItemStatuses } from "../../../hooks/useItemStatus";
import {
  CreateItemStatusRequest,
  ItemStatus,
  UpdateItemStatusRequest,
} from "../../../types/admin";

interface ItemStatusFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  status?: ItemStatus;
  onSubmit: (data: CreateItemStatusRequest | UpdateItemStatusRequest) => void;
  onClose: () => void;
}

const ItemStatusFormModal: React.FC<ItemStatusFormModalProps> = ({
  open,
  mode,
  status,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateItemStatusRequest>({
    name: "",
    displayName: "",
    description: "",
    color: "#1976d2",
    icon: "",
    sortOrder: 0,
    isActive: true,
    canTransitionTo: [],
    requiresTrackingNumber: false,
    requiresNote: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch all item statuses for transition selection
  const { data: allStatusesData } = useItemStatuses({
    pageNumber: 1,
    pageSize: 1000,
  });
  const availableStatuses = allStatusesData?.data?.statuses || [];

  useEffect(() => {
    if (mode === "edit" && status) {
      setFormData({
        name: status.name,
        displayName: status.displayName,
        description: status.description || "",
        color: status.color,
        icon: status.icon || "",
        sortOrder: status.sortOrder,
        isActive: status.isActive,
        canTransitionTo: status.canTransitionTo || [],
        requiresTrackingNumber: status.requiresTrackingNumber,
        requiresNote: status.requiresNote,
      });
    } else {
      setFormData({
        name: "",
        displayName: "",
        description: "",
        color: "#1976d2",
        icon: "",
        sortOrder: 0,
        isActive: true,
        canTransitionTo: [],
        requiresTrackingNumber: false,
        requiresNote: false,
      });
    }
    setErrors({});
  }, [mode, status, open]);

  const handleInputChange = (
    field: keyof CreateItemStatusRequest,
    value: any,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t("admin.nameRequired");
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = t("admin.displayNameRequired");
    }

    if (!formData.color) {
      newErrors.color = t("admin.colorRequired");
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = t("admin.sortOrderNonNegative");
    }

    // Validate transitions - check if all selected transition IDs exist
    if (formData.canTransitionTo.length > 0) {
      const validStatusIds = availableStatuses.map((s: ItemStatus) => s.id);
      const invalidIds = formData.canTransitionTo.filter(
        (id) => !validStatusIds.includes(id),
      );
      if (invalidIds.length > 0) {
        newErrors.canTransitionTo = `Invalid transition IDs: ${invalidIds.join(
          ", ",
        )}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const predefinedColors = [
    "#1976d2",
    "#dc004e",
    "#9c27b0",
    "#673ab7",
    "#3f51b5",
    "#2196f3",
    "#03a9f4",
    "#00bcd4",
    "#009688",
    "#4caf50",
    "#8bc34a",
    "#cddc39",
    "#ffeb3b",
    "#ffc107",
    "#ff9800",
    "#ff5722",
    "#795548",
    "#607d8b",
    "#f44336",
    "#e91e63",
  ];

  const commonIcons = [
    "📦",
    "🚚",
    "✅",
    "❌",
    "⏳",
    "🔄",
    "📋",
    "📝",
    "🔍",
    "⚠️",
    "🎯",
    "⭐",
    "💎",
    "🔥",
    "💡",
    "🔧",
    "📊",
    "📈",
    "📉",
    "🎉",
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {mode === "create" ? t("admin.createItemStatus") : t("admin.editItemStatus")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            {/* Basic Information */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.nameWithAsterisk")}
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
              />
              <TextField
                label={t("admin.displayNameWithAsterisk")}
                value={formData.displayName}
                onChange={(e) =>
                  handleInputChange("displayName", e.target.value)
                }
                error={!!errors.displayName}
                helperText={errors.displayName}
                fullWidth
              />
            </Box>

            <TextField
              label={t("admin.description")}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            {/* Visual Settings */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.colorWithAsterisk")}
                value={formData.color}
                onChange={(e) => handleInputChange("color", e.target.value)}
                error={!!errors.color}
                helperText={errors.color}
                sx={{ width: 150 }}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1 }}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleInputChange("color", color)}
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: color,
                      borderRadius: "50%",
                      cursor: "pointer",
                      border:
                        formData.color === color
                          ? "2px solid #000"
                          : "1px solid #ccc",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.icon")}
                value={formData.icon}
                onChange={(e) => handleInputChange("icon", e.target.value)}
                placeholder={t("admin.iconPlaceholder")}
                sx={{ width: 150 }}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1 }}>
                {commonIcons.map((icon) => (
                  <Chip
                    key={icon}
                    label={icon}
                    onClick={() => handleInputChange("icon", icon)}
                    variant={formData.icon === icon ? "filled" : "outlined"}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
            </Box>

            {/* Settings */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.sortOrder")}
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  handleInputChange("sortOrder", parseInt(e.target.value) || 0)
                }
                error={!!errors.sortOrder}
                helperText={errors.sortOrder}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange("isActive", e.target.checked)
                    }
                  />
                }
                label={t("common.active")}
              />
            </Box>

            {/* Requirements */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("admin.requirements")}
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requiresTrackingNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "requiresTrackingNumber",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={t("admin.requiresTrackingNumber")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requiresNote}
                      onChange={(e) =>
                        handleInputChange("requiresNote", e.target.checked)
                      }
                    />
                  }
                  label={t("admin.requiresNote")}
                />
              </Box>
            </Box>

            {/* Allowed Transitions */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("admin.allowedTransitions")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("admin.allowedTransitionsHelperText")}
              </Typography>
              <Autocomplete
                multiple
                options={availableStatuses.filter((s: ItemStatus) => s.id !== status?.id)}
                getOptionLabel={(option) =>
                  `${option.displayName} (${option.name})`
                }
                value={availableStatuses.filter((s: ItemStatus) =>
                  formData.canTransitionTo.includes(s.id),
                )}
                onChange={(_, newValue) => {
                  handleInputChange(
                    "canTransitionTo",
                    newValue.map((s: ItemStatus) => s.id),
                  );
                }}
                renderTags={(value: ItemStatus[], getTagProps) =>
                  value.map((option: ItemStatus, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.displayName}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("admin.selectAllowedTransitions")}
                    error={!!errors.canTransitionTo}
                    helperText={errors.canTransitionTo}
                  />
                )}
                disabled={availableStatuses.length === 0}
              />
              {availableStatuses.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t("admin.noStatusesAvailableForTransitions")}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t("common.cancel")}</Button>
          <Button type="submit" variant="contained">
            {mode === "create" ? t("common.create") : t("common.update")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ItemStatusFormModal;
